<template>
  <div class="wp-reviewer-comments">
    <div
      class="wp-reviewer-comments__grid"
      :class="{
        'wp-reviewer-comments__grid--single': !showExternalColumn,
        'wp-reviewer-comments__grid--split': isSplitTextareas,
      }"
    >
      <div
        v-if="showInternalHeader"
        class="wp-reviewer-comments__head-internal"
      >
        <p class="workpermit-content-slot__review_hint">
          {{ firstCellHeaderDisplay }}
        </p>
      </div>
      <div
        v-if="showExternalColumn"
        class="wp-reviewer-comments__head-external"
      >
        <div class="wp-reviewer-comments__external-head">
          <p class="workpermit-content-slot__review_hint">
            {{ externalHeaderDisplay }}
          </p>
        </div>
      </div>
      <div
        class="wp-reviewer-comments__cell-internal"
        :class="{ 'wp-reviewer-comments__cell-internal--full': cellInternalFullWidth }"
      >
        <TextField
          class="workpermit-content-slot__review-text-input"
          :showColon="false"
          :required="internalRequired"
          :readonly="readonly"
          :readonlyAsInput="readonly"
          :placeholder="firstCellPlaceholder"
          :extendClass="firstCellRejectErrorClass"
          :value="internalValue"
          @updateValue="val => $emit('update:internalValue', val)"
        />
      </div>
      <div
        v-if="showExternalColumn && showExternalTextarea"
        class="wp-reviewer-comments__cell-external"
      >
        <TextField
          class="workpermit-content-slot__review-text-input"
          :showColon="false"
          :required="leaveExternalValue"
          :readonly="readonly"
          :readonlyAsInput="readonly"
          :placeholder="$t('workPermit.externalCommentsPlaceholder')"
          :value="externalValue"
          :extendClass="externalError ? 'wp-reviewer-comments__external-text--reject-error' : ''"
          @updateValue="val => $emit('update:externalValue', val)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TextField from '@/controls/TextField'

export default {
  name: 'WpReviewerCommentFields',
  components: { TextField },
  props: {
    /** Left column heading (e.g. “Please input comments and acknowledge/reject”). Falls back to internalCommentsTitle. */
    internalSectionTitle: {
      type: String,
      default: '',
    },
    /** Right column heading. Falls back to workPermit.externalCommentsTitle. */
    externalSectionTitle: {
      type: String,
      default: '',
    },
    internalValue: {
      type: String,
      default: '',
    },
    externalValue: {
      type: String,
      default: '',
    },
    leaveExternalValue: {
      type: Boolean,
      default: false,
    },
    /** When false, hide “Leave comments for Contractor” (e.g. final WP approver). Readonly history with external text still shows. */
    allowLeaveExternalComments: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    internalRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * author: alvis
     * timestamp: 2026-04-22
     * description: When true, the external TextField is styled as error (reject with empty required external). Uses a custom class, not `fusion-input-check-is-invalid-input`, so `isAllFieldsValid` does not block `validation()` on a second Reject click.
     * parameter: {Boolean} externalError — highlight external field (reject, empty required)
     */
    externalError: {
      type: Boolean,
      default: false,
    },
    /**
     * 僅一欄可編輯、外欄隱藏，但上層 `buildHistoryDetail` 把此欄寫入 `externalComments` 時，改顯示對外標題（仍用 internal v-model 綁定 `actionComment`）。
     */
    soleFieldIsExternal: {
      type: Boolean,
      default: false,
    },
    /** When true with soleFieldIsExternal, hide section heading/placeholder (modal already labels the field). */
    omitSoleFieldChrome: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    internalHeaderDisplay () {
      if (this.internalSectionTitle != null && String(this.internalSectionTitle).trim() !== '') {
        return this.internalSectionTitle
      }
      return this.$t('workPermit.internalCommentsTitle')
    },
    externalHeaderDisplay () {
      if (this.externalSectionTitle != null && String(this.externalSectionTitle).trim() !== '') {
        return this.externalSectionTitle
      }
      return this.$t('workPermit.externalCommentsTitle')
    },
    /** Editable: show external column when allowed; readonly: only if external comment was saved */
    showExternalColumn () {
      if (!this.allowLeaveExternalComments && !this.readonly) return false
      if (!this.readonly) return true
      return this.leaveExternalValue && String(this.externalValue || '').trim().length > 0
    },
    showExternalTextarea () {
      if (!this.showExternalColumn) return false
      if (this.readonly) return true
      return true
    },
    /** Internal comment spans full width under headers when external textarea is not shown (editable) */
    cellInternalFullWidth () {
      if (this.readonly) return !this.showExternalTextarea
      if (this.isSplitTextareas) return false
      return true
    },
    isSplitTextareas () {
      return this.showExternalColumn && this.showExternalTextarea
    },
    useExternalCopyForSoleTextarea () {
      return this.soleFieldIsExternal && !this.readonly && !this.showExternalColumn
    },
    /** Modal-only: parent already shows reject label; detail page keeps sole-field heading. */
    hideSoleFieldChrome () {
      return this.useExternalCopyForSoleTextarea && this.omitSoleFieldChrome
    },
    showInternalHeader () {
      return !this.hideSoleFieldChrome
    },
    firstCellHeaderDisplay () {
      if (this.useExternalCopyForSoleTextarea) {
        return this.externalHeaderDisplay
      }
      return this.internalHeaderDisplay
    },
    firstCellPlaceholder () {
      if (this.useExternalCopyForSoleTextarea) {
        return this.$t('workPermit.externalCommentsPlaceholder')
      }
      return this.$t('workPermit.internalCommentsPlaceholder')
    },
    /**
     * 拒絕時必填之「對外」內容在 sole-field 路徑寫在左欄：與外欄的 externalError 一樣顯示紅框，且不併入 fusion 整表 invalid（見 externalError 註解）。
     */
    firstCellRejectErrorClass () {
      if (!this.externalError || !this.useExternalCopyForSoleTextarea) {
        return ''
      }
      return 'wp-reviewer-comments__external-text--reject-error'
    },
  },
  watch: {
    allowLeaveExternalComments (allowed) {
      if (allowed === false && !this.readonly && String(this.externalValue || '').trim() !== '') {
        this.$emit('update:externalValue', '')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/workpermit/style';

$wp-reviewer-comments-bp: 640px;

.wp-reviewer-comments {
  /* Match TextField: readonlyAsInput === disabled; must mirror `readonly`, not `!readonly`. */
  &__grid {
    display: grid;
    width: 100%;
    column-gap: 24px;
    align-items: stretch;
    /* Narrow: stack in DOM order (internal head → internal box → external head → external box) */
    grid-template-columns: minmax(0, 1fr);

    &--single {
      grid-template-columns: minmax(0, 1fr);
    }

    /* Wide: 50% / 50% */
    &:not(.wp-reviewer-comments__grid--single) {
      @media (min-width: $wp-reviewer-comments-bp) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-template-rows: auto 1fr;
        row-gap: 0;
      }
    }
  }

  &__cell-internal,
  &__cell-external {
    display: flex;
    flex-direction: column;
    min-height: 0;

    & ::v-deep .text-field {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    & ::v-deep .row.text-input {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    & ::v-deep .text-input textarea.autosize {
      flex: 1 1 auto;
      width: 100%;
      min-height: 74px;
      box-sizing: border-box;
    }
  }

  &__head-internal {
    align-self: start;
    min-width: 0;
    .workpermit-content-slot__review_hint {
      margin-bottom: 8px;
      font-weight: bold;
      font-size: 14px;
    }

    .wp-reviewer-comments__grid:not(.wp-reviewer-comments__grid--single) & {
      @media (min-width: $wp-reviewer-comments-bp) {
        grid-column: 1;
        grid-row: 1;
      }
    }
  }

  &__head-external {
    align-self: start;
    min-width: 0;
    .workpermit-content-slot__review_hint {
      margin-bottom: 8px;
      font-weight: bold;
      font-size: 14px;
    }

    .wp-reviewer-comments__grid:not(.wp-reviewer-comments__grid--single) & {
      @media (min-width: $wp-reviewer-comments-bp) {
        grid-column: 2;
        grid-row: 1;
      }
    }
  }

  &__cell-internal {
    min-width: 0;

    & ::v-deep textarea.wp-reviewer-comments__external-text--reject-error {
      background-color: #f9ecea !important;
    }

    .wp-reviewer-comments__grid:not(.wp-reviewer-comments__grid--single) & {
      @media (min-width: $wp-reviewer-comments-bp) {
        grid-column: 1;
        grid-row: 2;
      }
    }

    &--full {
      .wp-reviewer-comments__grid:not(.wp-reviewer-comments__grid--single) & {
        @media (min-width: $wp-reviewer-comments-bp) {
          grid-column: 1 / -1;
        }
      }
    }
  }

  &__cell-external {
    min-width: 0;

    .wp-reviewer-comments__grid:not(.wp-reviewer-comments__grid--single) & {
      @media (min-width: $wp-reviewer-comments-bp) {
        grid-column: 2;
        grid-row: 2;
      }
    }

    /* Visually same as .fusion-input-check-is-invalid-input; own class to avoid counting as fusion invalid. */
    & ::v-deep textarea.wp-reviewer-comments__external-text--reject-error {
      background-color: #f9ecea !important;
    }
  }

  &__external-head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 8px 16px;
    .workpermit-content-slot__review_hint {
      flex: 1 1 auto;
      margin-bottom: 0;
      font-weight: bold;
      font-size: 14px;
    }
  }

}
</style>
